import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { CustomFieldTypeEnum } from 'src/app/enums/customFieldType.enum';
import { JobDictionaryConfig } from 'src/app/models/jobDictionaryConfig.model';
import { TenantRisk } from 'src/app/models/tenantRisk.model';
import { TenantRiskService } from 'src/app/services/tenantRisk.service';
import { ToastService } from 'src/app/services/toast.service';
import { trackByIndex } from 'src/app/shared/utils/track-by.util';

interface SourceFieldOption {
    value: CustomFieldTypeEnum;
    label: string;
}

@Component({
  standalone: false,
    selector: 'app-job-dictionary-config-dialog',
    templateUrl: './job-dictionary-config-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./job-dictionary-config-dialog.component.scss'],
})
export class JobDictionaryConfigDialogComponent implements OnInit, OnDestroy {
    readonly sourceFieldOptions: SourceFieldOption[] = [
        { value: CustomFieldTypeEnum.HrisJobCode, label: 'Job Code' },
        { value: CustomFieldTypeEnum.HrisJobName, label: 'Job Name' },
        { value: CustomFieldTypeEnum.HrisPosition, label: 'Position' }
    ];

    sourceKeyFieldType: CustomFieldTypeEnum | null = null;
    sourceNameFieldType: CustomFieldTypeEnum | null = null;

    isLoading = false;
    hasChanges = false;
    canSave = false;

    private tenantRisk: TenantRisk | null = null;
    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly dialogRef: MatDialogRef<JobDictionaryConfigDialogComponent>,
        private readonly tenantRiskService: TenantRiskService,
        private readonly toastService: ToastService
    ) { }

    ngOnInit(): void {
        this.loadConfiguration();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private updateCanSave(): void {
        this.canSave = this.sourceKeyFieldType != null && this.hasChanges && !this.isLoading;
    }

    onFieldChange(): void {
        this.hasChanges = true;
        this.updateCanSave();
    }

    private loadConfiguration(): void {
        this.isLoading = true;
        this.tenantRiskService.getCurrent().pipe(takeUntil(this.destroy$)).subscribe({
            next: (tenantRisk) => {
                this.tenantRisk = tenantRisk;
                const config = tenantRisk?.jobDictionaryConfig;
                this.sourceKeyFieldType = config?.sourceKeyFieldType ?? null;
                this.sourceNameFieldType = config?.sourceNameFieldType ?? null;
                this.hasChanges = false;
                this.isLoading = false;
                this.updateCanSave();
            },
            error: (error) => {
                this.toastService.showError(error);
                this.isLoading = false;
                this.updateCanSave();
            }
        });
    }

    save(): void {
        if (!this.tenantRisk || this.sourceKeyFieldType == null || this.isLoading) {
            return;
        }
        this.isLoading = true;
        this.updateCanSave();

        const jobDictionaryConfig: JobDictionaryConfig = {
            sourceKeyFieldType: this.sourceKeyFieldType,
            sourceNameFieldType: this.sourceNameFieldType ?? null
        };

        // Spread the loaded TenantRisk and set ONLY jobDictionaryConfig so riskLevels /
        // Bayesian / sampleValidationConfig JSON fields survive the round-trip.
        const payload: TenantRisk = { ...this.tenantRisk, jobDictionaryConfig };

        this.tenantRiskService.update(payload).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                this.toastService.showSuccess('Job Dictionary configuration saved successfully', true);
                this.dialogRef.close(true);
            },
            error: (error) => {
                this.toastService.showError(error);
                this.isLoading = false;
                this.updateCanSave();
            }
        });
    }

    cancel(): void {
        this.dialogRef.close(false);
    }

    trackByIndex = trackByIndex;
}
